import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Target, Swords, Trophy, Users, Timer, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';

const KEYBOARD_LAYOUT = [
  { row: 0, keys: ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'] },
  { row: 1, keys: ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'] },
  { row: 2, keys: ['Z', 'X', 'C', 'V', 'B', 'N', 'M'] },
  { row: 3, keys: [' '] }
];

const FINGER_MAP = {
  'Q': 4, 'A': 4, 'Z': 4, 'W': 3, 'S': 3, 'X': 3,
  'E': 2, 'D': 2, 'C': 2, 'R': 1, 'F': 1, 'V': 1, 'T': 1, 'G': 1, 'B': 1,
  'Y': 1, 'H': 1, 'N': 1, 'U': 1, 'J': 1, 'M': 1,
  'I': 2, 'K': 2, 'O': 3, 'L': 3, 'P': 4, ' ': 1
};

const FINGER_COLORS = {
  1: 'bg-red-500/60 border-red-500',
  2: 'bg-yellow-500/60 border-yellow-500',
  3: 'bg-green-500/60 border-green-500',
  4: 'bg-purple-500/60 border-purple-500'
};

const DuelPage = () => {
  const { duelId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  
  const [duel, setDuel] = useState(null);
  const [classmates, setClassmates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChallenge, setShowChallenge] = useState(!duelId);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  
  // Typing state
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [keystrokes, setKeystrokes] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [activeKey, setActiveKey] = useState('');
  const [lastKeyCorrect, setLastKeyCorrect] = useState(null);

  useEffect(() => {
    if (duelId) {
      fetchDuel();
    } else {
      fetchClassmates();
    }
  }, [duelId]);

  const fetchDuel = async () => {
    try {
      const token = localStorage.getItem('typebots_token');
      const response = await axios.get(`${API}/duels/${duelId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuel(response.data);
      setShowChallenge(false);
    } catch (err) {
      console.error('Failed to fetch duel:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassmates = async () => {
    try {
      const token = localStorage.getItem('typebots_token');
      const response = await axios.get(`${API}/classmates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassmates(response.data);
    } catch (err) {
      console.error('Failed to fetch classmates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDuel = async () => {
    if (!selectedOpponent) return;
    try {
      const token = localStorage.getItem('typebots_token');
      const response = await axios.post(`${API}/duels`, {
        opponent_id: selectedOpponent.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/duel/${response.data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create duel');
    }
  };

  const handleAcceptDuel = async () => {
    try {
      const token = localStorage.getItem('typebots_token');
      await axios.post(`${API}/duels/${duelId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDuel();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to accept duel');
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (isComplete || !duel || duel.status !== 'active') return;
    const key = e.key;
    if (key.length === 1 || key === ' ') {
      setActiveKey(key.toUpperCase());
      setTimeout(() => setActiveKey(''), 150);
    }
  }, [isComplete, duel]);

  const handleInput = async (e) => {
    if (isComplete || !duel || duel.status !== 'active') return;
    
    const phrase = duel.phrase.toLowerCase();
    const newText = e.target.value.toLowerCase();
    const expectedChar = phrase[currentIndex];
    const typedChar = newText[newText.length - 1];
    
    if (!startTime) setStartTime(Date.now());
    setKeystrokes(prev => prev + 1);
    
    if (typedChar === expectedChar) {
      setCorrectKeystrokes(prev => prev + 1);
      setLastKeyCorrect(true);
      setTypedText(newText);
      setCurrentIndex(prev => prev + 1);
      
      if (newText.length === phrase.length) {
        completeSession(newText);
      }
    } else {
      setLastKeyCorrect(false);
    }
  };

  const completeSession = async (finalText) => {
    setIsComplete(true);
    const duration = (Date.now() - startTime) / 1000;
    
    try {
      const token = localStorage.getItem('typebots_token');
      await axios.post(`${API}/typing/session`, {
        phrase: duel.phrase,
        typed_text: finalText,
        keystrokes: keystrokes + 1,
        correct_keystrokes: correctKeystrokes + 1,
        duration_seconds: duration,
        is_duel: true,
        duel_id: duelId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      refreshUser();
      setTimeout(() => fetchDuel(), 1000);
    } catch (err) {
      console.error('Failed to save session:', err);
    }
  };

  const accuracy = keystrokes > 0 ? Math.round((correctKeystrokes / keystrokes) * 100) : 100;
  const nextKey = duel ? duel.phrase[currentIndex]?.toUpperCase() || '' : '';
  const isChallenger = duel?.challenger_id === user?.id;
  const myAccuracy = isChallenger ? duel?.challenger_accuracy : duel?.opponent_accuracy;
  const theirAccuracy = isChallenger ? duel?.opponent_accuracy : duel?.challenger_accuracy;

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-space flex items-center justify-center">
        <div className="text-bot-cyan animate-pulse font-display text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-space">
      <div className="grid-bg" />
      
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-panel/90 backdrop-blur-md border-b border-mid-panel">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-screen-white/60 hover:text-screen-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body">Back</span>
          </Link>
          
          <div className="font-display text-xl flex items-center gap-2">
            <Swords className="w-6 h-6 text-glitch-red" />
            <span className="text-glitch-red">GLITCH DUEL</span>
          </div>
          
          <div className="w-20" />
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Challenge Selection */}
          {showChallenge && (
            <div className="bg-dark-panel border-2 border-glitch-red/30 rounded-xl p-8" data-testid="duel-challenge-panel">
              <div className="text-center mb-8">
                <Swords className="w-16 h-16 text-glitch-red mx-auto mb-4" />
                <h1 className="font-display text-4xl text-screen-white mb-2">CHALLENGE A CLASSMATE</h1>
                <p className="font-body text-screen-white/60">30-keystroke accuracy showdown!</p>
              </div>
              
              {classmates.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-screen-white/30 mx-auto mb-4" />
                  <p className="font-body text-screen-white/50">No classmates available to duel</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {classmates.map((mate) => (
                      <button
                        key={mate.id}
                        onClick={() => setSelectedOpponent(mate)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          selectedOpponent?.id === mate.id
                            ? 'bg-glitch-red/20 border-glitch-red'
                            : 'bg-deep-space border-mid-panel hover:border-glitch-red/50'
                        }`}
                        data-testid={`opponent-${mate.id}`}
                      >
                        <div
                          className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-deep-space font-bold text-xl"
                          style={{ backgroundColor: mate.pixel_color }}
                        >
                          {mate.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="font-body text-sm text-screen-white">{mate.username}</div>
                        <div className="font-mono text-xs text-screen-white/50">Stage {mate.stage}</div>
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={handleCreateDuel}
                    disabled={!selectedOpponent}
                    className="w-full font-display uppercase tracking-wider bg-glitch-red text-white px-8 py-4 rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#991B2E] transition-all disabled:opacity-50"
                    data-testid="start-duel-btn"
                  >
                    Challenge {selectedOpponent?.username || 'Someone'}
                  </button>
                </>
              )}
            </div>
          )}
          
          {/* Active Duel */}
          {duel && !showChallenge && (
            <>
              {/* Duel Header */}
              <div className="bg-dark-panel border-2 border-glitch-red/30 rounded-xl p-6 mb-6" data-testid="duel-header">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <div className="font-display text-xl text-screen-white">{duel.challenger_username}</div>
                    <div className="font-display text-3xl text-bot-cyan mt-2">
                      {duel.challenger_accuracy !== null ? `${duel.challenger_accuracy.toFixed(1)}%` : '---'}
                    </div>
                    {duel.challenger_id === user?.id && <span className="font-mono text-xs text-zap-yellow">(You)</span>}
                  </div>
                  
                  <div className="px-6">
                    <div className="font-display text-3xl text-glitch-red">VS</div>
                    <div className="font-mono text-xs text-screen-white/50 mt-2">
                      {duel.status === 'pending' ? 'PENDING' : duel.status === 'active' ? 'ACTIVE' : 'COMPLETE'}
                    </div>
                  </div>
                  
                  <div className="text-center flex-1">
                    <div className="font-display text-xl text-screen-white">{duel.opponent_username}</div>
                    <div className="font-display text-3xl text-bot-cyan mt-2">
                      {duel.opponent_accuracy !== null ? `${duel.opponent_accuracy.toFixed(1)}%` : '---'}
                    </div>
                    {duel.opponent_id === user?.id && <span className="font-mono text-xs text-zap-yellow">(You)</span>}
                  </div>
                </div>
                
                {/* Winner */}
                {duel.status === 'completed' && duel.winner_id && (
                  <div className="mt-6 text-center">
                    <Trophy className="w-12 h-12 text-zap-yellow mx-auto mb-2" />
                    <div className="font-display text-2xl text-zap-yellow">
                      {duel.winner_id === user?.id ? 'YOU WIN!' : 'You lost...'}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Accept Duel Button */}
              {duel.status === 'pending' && duel.opponent_id === user?.id && (
                <div className="bg-dark-panel border-2 border-zap-yellow rounded-xl p-6 mb-6 text-center">
                  <AlertTriangle className="w-12 h-12 text-zap-yellow mx-auto mb-4" />
                  <p className="font-body text-screen-white mb-4">You've been challenged!</p>
                  <button
                    onClick={handleAcceptDuel}
                    className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-8 py-3 rounded-sm"
                    data-testid="accept-duel-btn"
                  >
                    Accept Challenge
                  </button>
                </div>
              )}
              
              {/* Typing Area */}
              {duel.status === 'active' && myAccuracy === null && (
                <>
                  <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-8 mb-6" data-testid="duel-phrase">
                    <div className="font-mono text-3xl tracking-wide text-center">
                      {duel.phrase.split('').map((char, i) => (
                        <span
                          key={i}
                          className={`${
                            i < currentIndex ? 'text-bot-cyan' :
                            i === currentIndex ? 'bg-zap-yellow/30 text-zap-yellow border-b-2 border-zap-yellow' :
                            'text-screen-white/30'
                          }`}
                        >
                          {char === ' ' ? '\u00A0' : char}
                        </span>
                      ))}
                    </div>
                    
                    <input
                      ref={inputRef}
                      type="text"
                      value={typedText}
                      onChange={handleInput}
                      onKeyDown={handleKeyDown}
                      className="absolute opacity-0 pointer-events-none"
                      autoFocus
                      data-testid="duel-input"
                    />
                    
                    <button
                      onClick={() => inputRef.current?.focus()}
                      className="w-full mt-4 py-3 border border-mid-panel rounded-lg text-screen-white/50 font-body text-sm hover:border-glitch-red"
                    >
                      Click here to start typing...
                    </button>
                  </div>
                  
                  {/* Mini Keyboard */}
                  <div className="bg-dark-panel border-2 border-mid-panel rounded-xl p-4">
                    {KEYBOARD_LAYOUT.map((row, rowIndex) => (
                      <div key={rowIndex} className={`flex justify-center gap-1 mb-1 ${rowIndex === 1 ? 'ml-4' : rowIndex === 2 ? 'ml-8' : ''}`}>
                        {row.keys.map((key) => {
                          const finger = FINGER_MAP[key];
                          const isNext = nextKey === key;
                          const isSpace = key === ' ';
                          
                          return (
                            <div
                              key={key}
                              className={`${isSpace ? 'w-32' : 'w-8'} h-8 rounded flex items-center justify-center font-mono text-xs border ${FINGER_COLORS[finger]} ${isNext ? 'ring-2 ring-zap-yellow animate-pulse' : ''}`}
                            >
                              {isSpace ? 'SPC' : key}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </>
              )}
              
              {/* Waiting for opponent */}
              {duel.status === 'active' && myAccuracy !== null && theirAccuracy === null && (
                <div className="bg-dark-panel border-2 border-bot-cyan rounded-xl p-8 text-center">
                  <Timer className="w-12 h-12 text-bot-cyan mx-auto mb-4 animate-spin" />
                  <p className="font-display text-xl text-screen-white">Waiting for opponent...</p>
                  <p className="font-body text-screen-white/60 mt-2">Your accuracy: {myAccuracy.toFixed(1)}%</p>
                  <p className="font-body text-screen-white/50 mt-1">Keep practising and challenge them again!</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default DuelPage;
